import { ref, computed, reactive, onErrorCaptured, openBlock, createElementBlock, normalizeClass, createElementVNode, createVNode, unref, isRef, createBlock, createCommentVNode } from "vue";
import _sfc_main$2 from "./option_bar/option_bar.vue.js";
import _sfc_main$1 from "./renderer/renderer.vue.js";
import { enumerateGroups } from "../lib/utils.js";
import { computedModel, cachedRef } from "../lib/utils_vue.js";
import { getComponentInfo } from "../lib/info.js";
import { SETTINGS_VERBOSE_KEY, SETTINGS_INDENT_KEY, SETTINGS_SCHEME_KEY, SETTINGS_BACKGROUND_KEY, SETTINGS_POSITIONING_KEY, SETTINGS_SIDEBAR_KEY, SETTINGS_THEME_KEY } from "../lib/constants.js";
import defaultSettings from "../settings.json.js";
/* empty css                */
const _hoisted_1 = { class: "dialtone-playground__start" };
const __default__ = {
  name: "DtcCombinator"
};
const _sfc_main = /* @__PURE__ */ Object.assign(__default__, {
  props: {
    /**
     * Target component.
     */
    component: {
      type: Object,
      required: true
    },
    /**
     * The dialtone-vue component documentation.
     */
    documentation: {
      type: Object,
      required: true
    },
    /**
     * Library of components that can be displayed in the renderer through slots.
     */
    library: {
      type: Object,
      default: () => {
      }
    },
    /**
     * The variants to select.
     * All keys will be displayed to choose from in the variant picker.
     * Pass a variant with the key 'default' to override the default variant.
     */
    variants: {
      type: Object,
      default: () => {
      }
    },
    /**
     * Activate 'blueprint' mode, to use a simple version of the combinator.
     * Used to display the component but provided limited options for interaction.
     */
    blueprint: {
      type: Boolean,
      default: false
    },
    rootClass: {
      type: String,
      default: ""
    },
    headerClass: {
      type: String,
      default: ""
    }
  },
  setup(__props) {
    const props = __props;
    const selectedVariant = ref("default");
    const isFullScreen = ref(false);
    const codePanel = ref();
    const info = computed(() => {
      return Object.freeze({
        ...initializeInfo(),
        members: {
          enumerate(handler) {
            enumerateGroups(handler, {
              slots: info.value.slots,
              props: info.value.props,
              attributes: info.value.attributes,
              events: info.value.events
            });
          }
        },
        bindings: {
          get() {
            const bindings = [];
            this.enumerate((_, binding) => bindings.push(binding));
            return bindings;
          },
          enumerate(handler) {
            var _a;
            enumerateGroups(handler, {
              props: info.value.props,
              attributes: (_a = info.value.attributes) == null ? void 0 : _a.filter((attribute) => attribute)
            });
          }
        }
      });
    });
    const options = computedModel(
      computed(() => {
        return reactive({
          ...getInitialValues(info.value),
          bindings: {
            get() {
              const bindings = [];
              this.enumerate((_, binding) => bindings.push(binding));
              return Object.fromEntries(bindings);
            },
            enumerate(handler) {
              enumerateGroups(handler, {
                props: options.value.props ? Object.entries(options.value.props) : null,
                attributes: options.value.attributes ? Object.entries(options.value.attributes) : null
              });
            }
          }
        });
      }),
      /**
       * Catch errors when updating member values,
       * else vue can block the value from ever being changed,
       * keeping the value permanently in an invalid state
       *
       * @param e
       * @param model
       */
      (e, model) => {
        try {
          e(model);
        } catch (exception) {
          console.warn("Update options warning: \n", exception);
        }
      }
    );
    const settings = computedModel(
      computed(() => {
        return reactive({
          root: {
            theme: cachedRef(SETTINGS_THEME_KEY, defaultSettings.root["default-theme"]),
            sidebar: cachedRef(SETTINGS_SIDEBAR_KEY, defaultSettings.root["default-sidebar"])
          },
          renderer: {
            positioning: cachedRef(SETTINGS_POSITIONING_KEY, defaultSettings.renderer["default-positioning"]),
            background: cachedRef(SETTINGS_BACKGROUND_KEY, defaultSettings.renderer["default-background"])
          },
          code: {
            scheme: cachedRef(SETTINGS_SCHEME_KEY, defaultSettings.code["default-scheme"]),
            indent: cachedRef(SETTINGS_INDENT_KEY, defaultSettings.code["default-indent-spaces"]),
            verbose: props.blueprint ? false : cachedRef(SETTINGS_VERBOSE_KEY, defaultSettings.code["default-verbose"])
          }
        });
      }),
      (e, model) => {
        try {
          e(model);
        } catch (exception) {
          console.warn("Update settings warning: \n", exception);
        }
      }
    );
    function initializeInfo() {
      var _a;
      const info2 = getComponentInfo(props.component, props.documentation);
      const variantInfo = (_a = props.variants) == null ? void 0 : _a[selectedVariant.value];
      if (variantInfo) {
        Object.entries(variantInfo).forEach(([memberGroup, members]) => {
          Object.entries(members).forEach(([memberName, member]) => {
            const infoMember = info2[memberGroup].find((infoMember2) => infoMember2.name === memberName);
            if (infoMember) {
              Object.assign(infoMember, member);
            }
          });
        });
      }
      return info2;
    }
    function getInitialValues(info2) {
      const options2 = {};
      info2.members.enumerate((memberGroup, member) => {
        options2[memberGroup] = options2[memberGroup] || {};
        options2[memberGroup][member.name] = member.initialValue;
      });
      return options2;
    }
    function triggerEvent(event, value) {
      codePanel.value.trigger(event, value);
    }
    function toggleFullScreen($event) {
      if ($event) {
        document.body.classList.add("d-of-hidden", "d-h100vh");
      } else {
        document.body.classList.remove("d-of-hidden", "d-h100vh");
      }
      isFullScreen.value = $event;
    }
    onErrorCaptured((exception) => {
      console.error("Internal vue error: \n", exception);
      return false;
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: normalizeClass(["dialtone-playground", { "dialtone-playground--fullscreen": isFullScreen.value }])
      }, [
        createElementVNode("div", _hoisted_1, [
          createVNode(_sfc_main$1, {
            settings: unref(settings),
            "onUpdate:settings": _cache[0] || (_cache[0] = ($event) => isRef(settings) ? settings.value = $event : null),
            class: "dialtone-playground__component",
            component: __props.component,
            info: info.value,
            options: unref(options),
            library: __props.library,
            onEvent: _cache[1] || (_cache[1] = (event, value) => triggerEvent(event, value))
          }, null, 8, ["settings", "component", "info", "options", "library"]),
          !__props.blueprint ? (openBlock(), createBlock(_sfc_main$2, {
            key: 0,
            options: unref(options),
            "onUpdate:options": _cache[2] || (_cache[2] = ($event) => isRef(options) ? options.value = $event : null),
            component: __props.component,
            info: info.value,
            onToggleFullScreen: toggleFullScreen
          }, null, 8, ["options", "component", "info"])) : createCommentVNode("", true)
        ]),
        _cache[3] || (_cache[3] = createElementVNode("div", { class: "dialtone-playground__end" }, null, -1))
      ], 2);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=combinator.vue.js.map
